// src/components/Withdraw.jsx
import React, { useEffect, useState } from "react";
import "./Withdraw.css";
import { supabase } from "../supabaseClient";
import { useAuth } from "../contexts/SupabaseAuthProvider";
import { TOTP, Secret } from "otpauth";

export default function Withdraw() {
  const { user } = useAuth();

  const NETWORKS = [
    { id: "usdt_trc", name: "USDT (TRC-20)", icon: "/pay/usdt.png" },
    { id: "usdt_erc", name: "USDT (ERC-20)", icon: "/pay/usdt.png" },
    { id: "binance", name: "Binance Pay", icon: "/pay/binance.png" },
    { id: "btc", name: "Bitcoin (BTC)", icon: "/pay/btc.png" },
    { id: "eth", name: "Ethereum (ETH)", icon: "/pay/eth.png" },
    { id: "ltc", name: "Litecoin (LTC)", icon: "/pay/ltc.png" },
  ];

  const MIN_WITHDRAW = 10;

  const [method, setMethod] = useState(null);
  const [amount, setAmount] = useState("");
  const [wallet, setWallet] = useState("");
  const [loading, setLoading] = useState(false);
  const [preLoading, setPreLoading] = useState(false);

  const [popup, setPopup] = useState(false);

  // 2FA STATE
  const [need2FA, setNeed2FA] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState("");
  const [otp, setOtp] = useState("");
  const [otpMsg, setOtpMsg] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const [balance, setBalance] = useState(0);
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalPayout, setTotalPayout] = useState(0);

  // safe array parser
  const safeArray = (v) => {
    if (!v) return [];
    if (!Array.isArray(v)) return [];
    return v.filter((x) => x && typeof x === "object");
  };

  // Load user data
  const loadData = async () => {
    if (!user?.id) return;
    try {
      const { data } = await supabase
        .from("users_data")
        .select("balance, withdraw_history, transactions, total_payout, settings")
        .eq("user_id", user.id)
        .single();

      if (!data) return;

      setBalance(Number(data.balance || 0));
      setWithdrawHistory(safeArray(data.withdraw_history));
      setTransactions(safeArray(data.transactions));
      setTotalPayout(Number(data.total_payout || 0));

      // load 2FA secret
      if (data.settings?.authEnabled) {
        setTwoFASecret(data.settings.authSecret || "");
      }

    } catch (err) {
      console.error("loadData err:", err);
    }
  };

  useEffect(() => {
    loadData();
  }, [user?.id]);


  // ──────────────────────────────────────────
  // Confirm withdrawal — but first check 2FA
  // ──────────────────────────────────────────
  const check2FA = () => {
    if (!method) return alert("Select a network");
    if (amount < MIN_WITHDRAW) return alert("Minimum withdrawal is $10");
    if (amount > balance) return alert("Not enough balance");
    if (!wallet) return alert("Enter wallet address");

    // mini loading effect
    setPreLoading(true);

    setTimeout(() => {
      setPreLoading(false);

      if (twoFASecret) {
        setNeed2FA(true);
        return;
      }

      confirmWithdraw();
    }, 600);
  };


  // ──────────────────────────────────────────
  // OTP VERIFY
  // ──────────────────────────────────────────
  const verifyOTP = () => {
    setOtpLoading(true);
    setOtpMsg("");

    setTimeout(() => {
      try {
        const totp = new TOTP({
          issuer: "MyApp Secure",
          label: user.email,
          secret: Secret.fromBase32(twoFASecret),
        });

        const delta = totp.validate({ token: otp });

        if (delta === null) {
          setOtpMsg("Invalid code");
          setOtpLoading(false);
          return;
        }

        setOtpLoading(false);
        setNeed2FA(false);
        confirmWithdraw();

      } catch (e) {
        setOtpMsg("Verification failed");
        setOtpLoading(false);
      }
    }, 600);
  };


  // ──────────────────────────────────────────
  // Withdraw request to DB
  // ──────────────────────────────────────────
  const confirmWithdraw = async () => {
    setLoading(true);

    const ID = `WD-${Date.now()}`;
    const TIME = new Date().toISOString();
    const amtNum = Number(amount);

    const withdrawItem = {
      id: ID,
      amount: amtNum,
      network: method.name,
      wallet,
      status: "pending",
      time: TIME,
    };

    const tx = {
      id: ID,
      amount: amtNum,
      type: "withdraw",
      network: method.name,
      wallet,
      status: "pending",
      time: TIME,
    };

    const newWH = [withdrawItem, ...withdrawHistory];
    const newTX = [tx, ...transactions];
    const newBalance = balance - amtNum;

    try {
      await supabase
        .from("users_data")
        .update({
          balance: newBalance,
          withdraw_history: newWH,
          transactions: newTX,
          updated_at: TIME,
        })
        .eq("user_id", user.id);

      setBalance(newBalance);
      setWithdrawHistory(newWH);
      setTransactions(newTX);
      setLoading(false);
      setPopup(true);

    } catch (err) {
      console.error("confirmWithdraw err:", err);
      alert("Withdraw failed");
      setLoading(false);
    }
  };


  return (
    <div className="wd-root">
      <div className="wd-card">
        <h2 className="wd-title">Withdraw Funds</h2>

        <div className="wd-account-box">
          <div className="wd-row">
            <span>In the account:</span>
            <strong>{balance.toFixed(2)} $</strong>
          </div>
          <div className="wd-row">
            <span>Available for withdrawal:</span>
            <strong>{balance.toFixed(2)} $</strong>
          </div>
          <div className="wd-row">
            <span>Commission:</span>
            <strong>0 $</strong>
          </div>
          <div className="wd-row">
            <span>Total Payout:</span>
            <strong>{totalPayout.toFixed(2)} $</strong>
          </div>
        </div>

        <label className="wd-label">Payment Method</label>
        <div className="wd-method-list">
          {NETWORKS.map((n) => (
            <button
              key={n.id}
              className={`wd-method ${method?.id === n.id ? "active" : ""}`}
              onClick={() => setMethod(n)}
            >
              <img src={n.icon} alt="" />
              {n.name}
            </button>
          ))}
        </div>

        <label className="wd-label">Amount</label>
        <div className="wd-amount-box">
          <input
            type="number"
            min="10"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter amount"
          />
          <div className="wd-unit">USD</div>
        </div>

        <label className="wd-label">Wallet Address</label>
        <input
          type="text"
          className="wd-wallet"
          value={wallet}
          onChange={(e) => setWallet(e.target.value)}
          placeholder="TRC20 / ERC20 / Binance ID"
        />

        <div className="wd-warning">
          ⚠ Ensure you enter the correct wallet address. Wrong network = permanent loss.
        </div>

        <button
          className={`wd-btn ${preLoading || loading ? "loading" : ""}`}
          disabled={preLoading || loading}
          onClick={check2FA}
        >
          {preLoading ? "Checking..." : loading ? "Processing..." : "Confirm Withdrawal"}
        </button>

        {popup && (
          <div className="wd-popup">
            <div className="wd-popup-card">
              <button className="wd-popup-close" onClick={() => setPopup(false)}>
                ×
              </button>
              <div className="wd-popup-icon">✓</div>
              <h3>Withdrawal Request Sent</h3>
              <p>Your withdrawal is now pending approval.</p>
            </div>
          </div>
        )}
      </div>


      {/* ===============================
                2FA POPUP
      =============================== */}
      {need2FA && (
        <div className="qx-2fa-overlay">
          <div className="qx-2fa-box">
            <h3 className="qx-2fa-title">Enter 2FA Code</h3>
            <p className="qx-2fa-text">
              Please enter your Google Authenticator 6-digit code.
            </p>

            <input
              type="text"
              maxLength={6}
              className="qx-2fa-input"
              placeholder="••••••"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            />

            {otpMsg && <p className="auth-error">{otpMsg}</p>}

            <button className="qx-btn qx-2fa-btn" onClick={verifyOTP} disabled={otpLoading}>
              {otpLoading ? <div className="qx-spinner"></div> : "Verify Withdrawal"}
            </button>

            <button className="qx-btn mt-2" onClick={() => setNeed2FA(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
